import type { ReactNode } from 'react';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper';
import { createDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn';
import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn';
import {
	createSingleLineHeader,
	SINGLE_LINE_STYLE,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/singleLine';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { extractDecimalPrecisionFromUnitDenominator } from '@dealstack/shared/lib/format/number';
import {
	CurrencyType,
	GetInvestorTransactionDetailsRes,
	LoanDefinitionType,
	SecurityType,
} from '@dealstack/shared/lib/generated';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import type { Row, Table } from '@tanstack/react-table';
import { ActionIcon, Anchor, Flex, Text } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import {
	TransactionTableColumn,
	type TransactionTableVestingTerm,
	type TransactionTableView,
	type TransactionTotals,
} from './transactionTable.types';
import {
	getTransactionVestingTermAccess,
	getVestingTermName,
	getTransactionTypeLabel,
	isLeaverTransaction,
	isSellTransactionType,
	prepareSignedTotalValue,
} from './transactionTable.utils';

interface Props {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	currency?: CurrencyType;
	vestingTerms?: TransactionTableVestingTerm[];
	mipId: string;
	view: TransactionTableView;
	getInvestmentReference: (
		investmentWorkflowType: string,
		isReleased: boolean,
		date: string
	) => string;
	getSecurityTypeTranslation: (type?: SecurityType | LoanDefinitionType) => string;
	onEditVestingTerm?: (transaction: GetInvestorTransactionDetailsRes) => void;
	expandableVestingTerm?: boolean;
	onVestingTermOpen?: () => void;
	totals?: TransactionTotals;
}

const truncatedHeader = (text: string, options?: { align?: 'left' | 'right' }) =>
	createSingleLineHeader<GetInvestorTransactionDetailsRes>(text, options);

const totalFooter = (content: ReactNode) => () => (
	<Flex justify="flex-end" w="100%">
		<Text fw={600} style={SINGLE_LINE_STYLE}>
			{content}
		</Text>
	</Flex>
);

const toggleOnlyRow = (
	row: Row<GetInvestorTransactionDetailsRes>,
	table: Table<GetInvestorTransactionDetailsRes>,
	onOpen?: () => void
) => {
	const isExpanded = row.getIsExpanded();

	table.resetExpanded();

	if (!isExpanded) {
		row.toggleExpanded(true);
		onOpen?.();
	}
};

export const getTransactionTableColumns = ({
	tMipManage,
	currency,
	vestingTerms,
	mipId,
	view,
	getInvestmentReference,
	getSecurityTypeTranslation,
	onEditVestingTerm,
	expandableVestingTerm = false,
	onVestingTermOpen,
	totals,
}: Props): ExtendedColumnDef<GetInvestorTransactionDetailsRes>[] => {
	return [
		createDateColumn<GetInvestorTransactionDetailsRes>({
			id: TransactionTableColumn.date,
			header: truncatedHeader(tMipManage('investors.expanded_row.date')),
			columnLabel: tMipManage('investors.expanded_row.date'),
			enableSorting: true,
			minSize: 120,
			accessorFn: (row) => (row.completedDate ? new Date(row.completedDate) : null),
		}),
		createTextColumn<GetInvestorTransactionDetailsRes>({
			id: TransactionTableColumn.transactionType,
			accessorKey: 'transactionType',
			header: truncatedHeader(tMipManage('investors.expanded_row.transaction_type')),
			columnLabel: tMipManage('investors.expanded_row.transaction_type'),
			enableSorting: true,
			minSize: 100,
			cell: ({ row }) => {
				const { transactionType } = row.original;
				return (
					<Text truncate style={SINGLE_LINE_STYLE}>
						{getTransactionTypeLabel(tMipManage, transactionType) ?? LONG_DASH}
					</Text>
				);
			},
		}),
		createTextColumn<GetInvestorTransactionDetailsRes>({
			id: TransactionTableColumn.securityType,
			header: truncatedHeader(tMipManage('investors.expanded_row.security_type')),
			columnLabel: tMipManage('investors.expanded_row.security_type'),
			enableSorting: true,
			minSize: 120,
			accessorFn: (row) => getSecurityTypeTranslation(row.securityType || row.loanType),
		}),
		createTextColumn<GetInvestorTransactionDetailsRes>({
			id: TransactionTableColumn.securityName,
			accessorKey: 'securityName',
			header: truncatedHeader(tMipManage('investors.expanded_row.security_name')),
			columnLabel: tMipManage('investors.expanded_row.security_name'),
			enableSorting: true,
			minSize: 120,
		}),
		createNumberColumn({
			id: TransactionTableColumn.totalVolume,
			accessorKey: 'totalVolume',
			header: truncatedHeader(tMipManage('investors.expanded_row.total_volume'), {
				align: 'right',
			}),
			columnLabel: tMipManage('investors.expanded_row.total_volume'),
			enableSorting: true,
			minSize: 120,
			...(totals && {
				footer: totalFooter(
					<AmountFormatted
						value={totals.volume}
						currency={currency}
						showCurrency={false}
						showZero
						showNegative
						decimalScale={totals.volumeDecimalScale}
					/>
				),
			}),
			cell: ({ row }) => {
				const transaction = row.original;
				const isSell = isSellTransactionType(transaction.transactionType);
				return (
					<Flex justify="flex-end" w="100%">
						<Text style={SINGLE_LINE_STYLE}>
							{transaction.totalVolume ? (
								<AmountFormatted
									value={transaction.totalVolume}
									currency={currency}
									showCurrency={false}
									withMinusSymbol={isSell}
									decimalScale={extractDecimalPrecisionFromUnitDenominator(
										transaction.securityUnitDenominator
									)}
								/>
							) : (
								tMipManage('investors.expanded_row.na')
							)}
						</Text>
					</Flex>
				);
			},
		}),
		createNumberColumn({
			id: TransactionTableColumn.totalValue,
			header: truncatedHeader(tMipManage('investors.expanded_row.total_cost'), {
				align: 'right',
			}),
			columnLabel: tMipManage('investors.expanded_row.total_cost'),
			enableSorting: true,
			accessorFn: prepareSignedTotalValue,
			sortingFn: 'basic',
			minSize: 120,
			...(totals && {
				footer: totalFooter(
					<AmountFormatted
						value={totals.cost}
						currency={currency}
						showZero
						showNegative
					/>
				),
			}),
			cell: ({ row }) => {
				const transaction = row.original;
				const isSell = isSellTransactionType(
					transaction.transactionType,
					transaction.isNegative
				);
				return (
					<Flex justify="flex-end" w="100%">
						<Text style={SINGLE_LINE_STYLE}>
							<AmountFormatted
								value={transaction.totalValue}
								currency={currency}
								withMinusSymbol={isSell}
							/>
						</Text>
					</Flex>
				);
			},
		}),
		createTextColumn<GetInvestorTransactionDetailsRes>({
			id: TransactionTableColumn.vestingTerm,
			header: truncatedHeader(tMipManage('investors.expanded_row.vesting_term')),
			columnLabel: tMipManage('investors.expanded_row.vesting_term'),
			enableSorting: false,
			cell: ({ row, table }) => {
				const transaction = row.original;
				const vestingTermAccess = getTransactionVestingTermAccess({
					transaction,
					hasVestingTerms: !!vestingTerms?.length,
					view,
				});
				const selectedVestingTermName = isLeaverTransaction(transaction)
					? LONG_DASH
					: getVestingTermName(vestingTerms, transaction);

				if (expandableVestingTerm && vestingTermAccess) {
					const linkText =
						selectedVestingTermName ??
						tMipManage('investors.details.holding_transactions.add_vesting_term');

					return (
						<Anchor
							component="button"
							type="button"
							fz={14}
							underline="always"
							maw="100%"
							title={linkText}
							onClick={() => toggleOnlyRow(row, table, onVestingTermOpen)}
							data-testid={makeComplexDataTestId(
								TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_TRANSACTION_VESTING_TERM_LINK,
								transaction.id
							)}
						>
							<Flex
								component="span"
								align="center"
								gap="0.25rem"
								wrap="nowrap"
								miw={0}
							>
								<Text component="span" inherit truncate style={SINGLE_LINE_STYLE}>
									{linkText}
								</Text>

								<Flex component="span" style={{ flexShrink: 0 }}>
									<Icon
										variant={row.getIsExpanded() ? 'caret-up' : 'caret-down'}
										size="lg"
									/>
								</Flex>
							</Flex>
						</Anchor>
					);
				}

				return (
					<Flex align="center" gap="0.5rem" wrap="nowrap" miw={0}>
						<Text truncate style={SINGLE_LINE_STYLE}>
							{selectedVestingTermName || LONG_DASH}
						</Text>
						{vestingTermAccess === 'edit' && onEditVestingTerm && (
							<PermissionWrapper
								resourceType={ResourceType.PROGRAMME}
								resourceId={mipId}
								permissionRole={PermissionRole.EDITOR}
							>
								<ActionIcon
									c="dark.4"
									style={{ flexShrink: 0 }}
									onClick={() => onEditVestingTerm(transaction)}
									data-testid={
										TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_INVESTMENT_EDIT_VESTING_BUTTON
									}
								>
									<Icon variant="pencil-edit-line" size="lg" />
								</ActionIcon>
							</PermissionWrapper>
						)}
					</Flex>
				);
			},
		}),
		createTextColumn<GetInvestorTransactionDetailsRes>({
			id: TransactionTableColumn.investmentReference,
			header: truncatedHeader(tMipManage('investors.expanded_row.investment_reference')),
			columnLabel: tMipManage('investors.expanded_row.investment_reference'),
			enableSorting: true,
			accessorFn: (row) =>
				row.loanType
					? LONG_DASH
					: getInvestmentReference(
							row.investmentWorkflowComputedType,
							!!row.completedDate,
							row.completedDate ?? row.createdDate
						),
		}),
	];
};
